<template>
  <BaseModal
    :show="show"
    title="Parental Consent Required"
    max-width="632"
    :persistent="!dismissable"
    :show-close="dismissable"
    header-style="font-size: 24px; line-height: 1.5; color: #191919;"
    header-class="font-weight-bold px-6 pt-6 pb-2 d-flex align-center justify-space-between"
    content-class="px-6 pt-2 pb-6"
    @update:show="$emit('update:show', $event)"
    @close="handleClose"
  >
    <!-- Intro -->
    <div class="mb-6" style="font-size: 16px; line-height: 1.5; color: #191919">
      Parental consent is required to enroll the minor
      <span style="font-weight: bold">{{ studentName || "[Student's Full Name]" }}</span>
      in this course or any future course.
    </div>

    <!-- Consent Checkbox Section -->
    <div class="mb-6">
      <v-checkbox
        v-model="consentChecked"
        hide-details
        color="black"
        class="minor-consent-checkbox"
        @change="asuoDataLayerClick('minorConsent', '0')"
      >
        <template #label>
          <div style="color: rgba(0, 0, 0, 0.54); font-size: 14px; line-height: 1.43">
            <p style="margin-bottom: 12px">
              I am the parent or legal guardian of the minor named above. I have the legal right to provide this consent
              and, by signing below, I acknowledge and agree to the following:
            </p>

            <ol style="margin: 16px 0; padding-left: 20px; list-style-type: decimal">
              <li style="margin-bottom: 12px">
                I understand that by enrolling my child in this course or any course in the future, they will be
                participating in college-level coursework, which may include advanced concepts, mature themes, or
                content that may not be suitable for all minors. Syllabi for all courses are available for review at
                <a
                  href="https://courses.ulc.asu.edu/"
                  style="color: #8c1d40; text-decoration: underline"
                  target="_blank"
                  rel="noopener"
                  @click.stop
                  >https://courses.ulc.asu.edu/</a
                >
              </li>

              <li style="margin-bottom: 12px">
                I consent to my child receiving educational support from ASU via SMS/text messages, automated calls,
                and/or prerecorded voice messages at the phone number and email address provided above. I also consent
                to ASU's mobile terms and conditions. I recognize that message and data rates may apply.
              </li>

              <li style="margin-bottom: 12px">
                I consent to my child's enrollment in this course or any course in the future and accept responsibility
                for reviewing and approving their participation.
              </li>

              <li style="margin-bottom: 12px">
                I consent to
                <a
                  href="https://www.asu.edu/about/privacy"
                  style="color: #8c1d40; text-decoration: underline"
                  target="_blank"
                  rel="noopener"
                  @click.stop
                  >ASU's Privacy Statements and supplements</a
                >
                and consent to my child's data being shared, where applicable, to third parties for the purposes of the
                administration of academic records and course participation.
              </li>

              <li style="margin-bottom: 12px">
                I consent for the perpetuity of my child's participation in this course or any course in the future.
                Should I wish to revoke participation in the future, I can reach out to the ULC support team at
                <a href="mailto:ulcourses@asu.edu" style="color: #8c1d40; text-decoration: underline" @click.stop
                  >ulcourses@asu.edu</a
                >. Request parent guest access
                <a
                  href="https://registrar.asu.edu/parent-guest-access"
                  style="color: #8c1d40; text-decoration: underline"
                  target="_blank"
                  rel="noopener"
                  @click.stop
                  >here</a
                >.
              </li>
            </ol>
          </div>
        </template>
      </v-checkbox>
    </div>

    <!-- Required Fields: Parent or Legal Guardian -->
    <div class="mb-4">
      <div class="font-weight-bold mb-2" style="font-size: 16px; color: #000000">Parent or Legal Guardian</div>

      <div class="d-flex flex-column flex-sm-row" style="gap: 24px">
        <div style="flex: 1; min-width: 0">
          <FormField label="First Name" required :error="firstNameError" v-slot="{ isInvalid, ariaDescribedby, id }">
            <v-text-field
              :id="id"
              v-model="firstName"
              placeholder="First Name"
              variant="outlined"
              density="comfortable"
              aria-required="true"
              :error="isInvalid"
              :aria-describedby="ariaDescribedby"
              hide-details
              style="font-size: 16px"
              @focusout="markFirstNameTouched"
            />
          </FormField>
        </div>

        <div style="flex: 1; min-width: 0">
          <FormField label="Last Name" required :error="lastNameError" v-slot="{ isInvalid, ariaDescribedby, id }">
            <v-text-field
              :id="id"
              v-model="lastName"
              placeholder="Last Name"
              variant="outlined"
              density="comfortable"
              aria-required="true"
              :error="isInvalid"
              :aria-describedby="ariaDescribedby"
              hide-details
              style="font-size: 16px"
              @focusout="markLastNameTouched"
            />
          </FormField>
        </div>
      </div>
    </div>

    <!-- Optional Section: ASU Outreach Consent + Email + Phone -->
    <div class="mb-6">
      <div class="mb-3" style="font-size: 14px; color: rgba(0, 0, 0, 0.6); line-height: 1.43">
        Optional: I consent to outreach from ASU re. ULCs, other educational opportunities, including discounts.
      </div>

      <div class="d-flex flex-column flex-sm-row" style="gap: 24px">
        <div style="flex: 1; min-width: 0">
          <FormField label="Parent Email" :error="emailError" v-slot="{ isInvalid, ariaDescribedby, id }">
            <v-text-field
              :id="id"
              v-model="email"
              placeholder="Email Address"
              variant="outlined"
              density="comfortable"
              :error="isInvalid"
              :aria-describedby="ariaDescribedby"
              hide-details
              style="font-size: 16px"
              @focusout="markEmailTouched"
            />
          </FormField>
        </div>

        <div style="flex: 1; min-width: 0">
          <FormField label="Phone Number" :error="phoneError" v-slot="{ isInvalid, ariaDescribedby, id }">
            <PhoneInput
              :id="id"
              v-model="phone"
              placeholder="Phone Number"
              :error="isInvalid"
              :aria-describedby="ariaDescribedby"
              @validate="onPhoneValidate"
              @blur="markPhoneTouched"
            />
          </FormField>
        </div>
      </div>
    </div>

    <!-- CTA Button -->
    <UiButton
      variant="gold"
      size="large"
      block
      :disabled="!isFormValid"
      :style="{ borderRadius: '100px', opacity: isFormValid ? 1 : 0.65 }"
      @click="
        handleConsent();
        asuoDataLayerClick('minorConsent', '1');
      "
    >
      Consent
    </UiButton>
  </BaseModal>
</template>

<script>
import { defineComponent, ref, computed } from 'vue';
import { BaseModal, Button as UiButton, PhoneInput, FormField } from './ui/index.js';
import { Helper } from '../mixins/helpers.js';
import { DataLayerMixin } from '../mixins/dataLayerMixin.js';

export default defineComponent({
  name: 'MinorConsentModal',

  components: {
    BaseModal,
    UiButton,
    PhoneInput,
    FormField
  },

  mixins: [Helper, DataLayerMixin],

  props: {
    show: {
      type: Boolean,
      default: false
    },
    studentName: {
      type: String,
      default: ''
    },
    dismissable: {
      type: Boolean,
      default: false
    }
  },

  emits: ['update:show', 'consent', 'cancel'],

  setup(_props, { emit }) {
    const consentChecked = ref(false);
    const firstName = ref('');
    const lastName = ref('');
    const email = ref('');
    const phone = ref('');

    const firstNameTouched = ref(false);
    const lastNameTouched = ref(false);
    const emailTouched = ref(false);
    const phoneTouched = ref(false);

    // Phone validity comes from PhoneInput's @validate (vue-tel-input), not from
    // parsing digits here — so it accepts any country's number.
    const phoneValid = ref(true);
    const phoneIsEmpty = ref(true);

    const onPhoneValidate = ({ isValid, isEmpty } = {}) => {
      phoneValid.value = Boolean(isValid);
      phoneIsEmpty.value = Boolean(isEmpty);
    };

    // Names must be non-empty and contain no digits (per the consent screen design).
    const firstNameHasNumbers = computed(() => /\d/.test(firstName.value));
    const lastNameHasNumbers = computed(() => /\d/.test(lastName.value));

    // Field errors: shown only after the first blur, then live as the user types.
    const firstNameError = computed(() => {
      if (!firstNameTouched.value) return '';
      if (firstName.value.trim() === '') return 'First name is required';
      if (firstNameHasNumbers.value) return "First Name can't contain numbers";
      return '';
    });
    const lastNameError = computed(() => {
      if (!lastNameTouched.value) return '';
      if (lastName.value.trim() === '') return 'Last name is required';
      if (lastNameHasNumbers.value) return "Last Name can't contain numbers";
      return '';
    });

    // Mark a required field touched once focus leaves it. Uses the native,
    // bubbling `focusout` event — `blur` doesn't bubble, so a listener on the
    // v-text-field's root wrapper never fires for the inner <input>.
    const markFirstNameTouched = () => {
      firstNameTouched.value = true;
    };
    const markLastNameTouched = () => {
      lastNameTouched.value = true;
    };
    const markEmailTouched = () => {
      emailTouched.value = true;
    };
    const markPhoneTouched = () => {
      phoneTouched.value = true;
    };

    // Optional fields: empty is allowed. Validity is computed ungated (drives the
    // Consent button); the *displayed* error is gated on first blur so it doesn't
    // fire while the user is still typing — matching the first/last name behaviour.
    const emailInvalid = computed(() => {
      const trimmed = email.value.trim();
      if (!trimmed) return false;
      return !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
    });
    const emailError = computed(() =>
      emailTouched.value && emailInvalid.value ? 'Please enter a valid email address' : ''
    );

    // A non-empty number must be a valid (international) number per vue-tel-input.
    const phoneInvalid = computed(() => !phoneIsEmpty.value && !phoneValid.value);
    const phoneError = computed(() => (phoneTouched.value && phoneInvalid.value ? 'Enter a valid phone number.' : ''));

    const isFormValid = computed(() => {
      return (
        consentChecked.value &&
        firstName.value.trim() !== '' &&
        !firstNameHasNumbers.value &&
        lastName.value.trim() !== '' &&
        !lastNameHasNumbers.value &&
        !emailInvalid.value &&
        !phoneInvalid.value
      );
    });

    const handleConsent = () => {
      if (isFormValid.value) {
        emit('consent', {
          firstName: firstName.value,
          lastName: lastName.value,
          email: email.value.trim() || undefined,
          phone: phone.value.trim() || undefined
        });
      }
    };

    const handleClose = () => {
      emit('cancel');
      emit('update:show', false);
      // Form state is intentionally preserved on dismiss
    };

    return {
      consentChecked,
      firstName,
      lastName,
      email,
      phone,
      firstNameTouched,
      lastNameTouched,
      emailTouched,
      phoneTouched,
      firstNameError,
      lastNameError,
      markFirstNameTouched,
      markLastNameTouched,
      markEmailTouched,
      markPhoneTouched,
      emailError,
      phoneError,
      phoneValid,
      phoneIsEmpty,
      onPhoneValidate,
      isFormValid,
      handleConsent,
      handleClose
    };
  }
});
</script>

<style scoped>
.minor-consent-checkbox :deep(.v-selection-control) {
  align-items: flex-start !important;
  min-height: auto;
}

.minor-consent-checkbox :deep(.v-selection-control__wrapper) {
  margin-top: 4px;
}

.minor-consent-checkbox :deep(.v-selection-control__input) {
  width: 24px;
  height: 24px;
  align-self: flex-start !important;
  margin-top: 0 !important;
}

.minor-consent-checkbox :deep(.v-label) {
  opacity: 1;
  margin-left: 8px;
}

:deep(.v-field--variant-outlined .v-field__outline__start),
:deep(.v-field--variant-outlined .v-field__outline__end) {
  border-color: #d0d0d0;
}

/* Error border for fields (the default rule above forces gray otherwise). */
:deep(.v-field--error.v-field--variant-outlined .v-field__outline__start),
:deep(.v-field--error.v-field--variant-outlined .v-field__outline__end) {
  border-color: #cc2f2f;
}
</style>
